import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { Box } from '@mui/material'
import debounce from 'lodash/debounce'
import { toast } from 'react-hot-toast'
import type { Draft } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import ComposeDraftHeader from 'src/components/Drafts/ComposeDraftHeader/ComposeDraftHeader'
import { UPDATE_DRAFT } from 'src/components/Drafts/mutations'
import { GET_DRAFT } from 'src/components/Drafts/queries'
import { draftReducer, type DraftState } from 'src/components/Drafts/reducer'
import SendDraftButton from 'src/components/Drafts/SendDraftButton/SendDraftButton'
import EmbeddedAiPageEditor from 'src/components/Page/EmbeddedAiPageEditor/EmbeddedAiPageEditor'
import { PageProvider, usePage } from 'src/hooks/usePage'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'

import SidebarLoader from '../SidebarLoader/SidebarLoader'

export const SidebarLayoutDraftInner = ({ draft }: { draft: Draft }) => {
  const { token, contextString, updatePageError, saving, page } = usePage()
  const latestDraftRef = useRef<DraftState>(null)

  const [draftState, dispatch] = useReducer(draftReducer, {
    draft: draft,
    status: 'idle',
  } as DraftState)

  // update on every render
  latestDraftRef.current = draftState

  // sync draft status with page status
  useEffect(() => {
    if (updatePageError) {
      dispatch({ type: 'ERROR_SAVING' })
    }
  }, [updatePageError])

  useEffect(() => {
    if (saving) {
      dispatch({ type: 'START_SAVING' })
    } else {
      dispatch({ type: 'STOP_SAVING' })
    }
  }, [saving])

  useEffect(() => {
    if (!page?.aiPopulationCompletedAt && page?.aiInitialPrompt) {
      dispatch({ type: 'PAGE_AI_UNDERWAY' })
    } else {
      dispatch({ type: 'PAGE_AI_NOT_UNDERWAY' })
    }
  }, [page?.aiPopulationCompletedAt, page?.aiInitialPrompt])

  const [updateDraft] = useMutation(UPDATE_DRAFT, {
    refetchQueries: [
      {
        query: GET_DRAFT,
        variables: { id: draft.id, workspaceId: draft.workspaceId },
      },
    ],
    onCompleted: () => {
      dispatch({
        type: 'STOP_SAVING',
      })
    },
    onError: (error) => {
      logger.error('Failed to update draft', error, {
        draftId: draft?.id,
        workspaceId: draft?.workspaceId,
      })
      toast.error('Failed to update draft')
      dispatch({
        type: 'ERROR_SAVING',
      })
    },
  })

  const debouncedSave = useMemo(() => {
    return debounce(() => {
      if (latestDraftRef.current.status === 'dirty') {
        updateDraft({
          variables: {
            workspaceId: latestDraftRef.current.draft.workspaceId,
            input: {
              id: latestDraftRef.current.draft.id,
              email: {
                subject: latestDraftRef.current.draft.email?.subject,
                to: latestDraftRef.current.draft.email?.to,
                cc: latestDraftRef.current.draft.email?.cc,
                bcc: latestDraftRef.current.draft.email?.bcc,
              },
            },
          },
        })
      }
    }, 1000)
  }, [updateDraft])

  const handleAddEmailRecipient = useCallback(
    (recipient: string, field: 'to' | 'cc' | 'bcc') => {
      dispatch({
        type: 'ADD_EMAIL_RECIPIENT',
        payload: { recipient, field },
      })
      dispatch({
        type: 'DEBOUNCE_STARTED',
      })
      debouncedSave()
    },
    [dispatch, debouncedSave]
  )

  const handleRemoveEmailRecipient = useCallback(
    (recipient: string, field: 'to' | 'cc' | 'bcc') => {
      dispatch({
        type: 'REMOVE_EMAIL_RECIPIENT',
        payload: { recipient, field },
      })
      dispatch({
        type: 'DEBOUNCE_STARTED',
      })
      debouncedSave()
    },
    [dispatch, debouncedSave]
  )

  const handleUpdateEmailSubject = useCallback(
    (subject: string) => {
      dispatch({
        type: 'UPDATE_EMAIL_SUBJECT',
        payload: { subject },
      })
      dispatch({
        type: 'DEBOUNCE_STARTED',
      })
      debouncedSave()
    },
    [dispatch, debouncedSave]
  )

  const handleUpdateEmailFrom = useCallback(
    (from: string) => {
      dispatch({
        type: 'UPDATE_EMAIL_FROM',
        payload: { from },
      })
      dispatch({
        type: 'DEBOUNCE_STARTED',
      })
      debouncedSave()
    },
    [dispatch, debouncedSave]
  )

  const handleDraftSent = useCallback(() => {
    dispatch({
      type: 'DRAFT_SENT',
    })
  }, [dispatch])

  return (
    <Box sx={{ height: '100%' }}>
      {token && contextString ? (
        <Box>
          <Box>
            <ComposeDraftHeader
              draft={draftState.draft}
              handleUpdateEmailFrom={handleUpdateEmailFrom}
              handleUpdateEmailSubject={handleUpdateEmailSubject}
              handleAddEmailRecipient={handleAddEmailRecipient}
              handleRemoveEmailRecipient={handleRemoveEmailRecipient}
              errorSaving={draftState.status === 'error'}
            />
            <Box
              sx={{
                mx: 3,
                p: 2,
                borderRadius: 2,
                display: 'flex',
                height: '400px',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <EmbeddedAiPageEditor
                key={`${draftState.draft.status}-${draftState.draft.id}`}
                parentObject={{
                  objectId: draftState.draft.id,
                  objectType: NativeObjectTypes.Draft,
                  workspaceId: draftState.draft.workspaceId,
                  properties: {
                    channel: 'EMAIL',
                    email: draftState.draft.email,
                  },
                }}
                userHasEditorAccess={draftState.draft.status !== 'SENT'}
              />
              <SendDraftButton
                draft={draftState.draft}
                handleDraftSent={handleDraftSent}
                status={draftState.status}
                pageAiUnderway={draftState.pageAiUnderway}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <SidebarLoader />
      )}
    </Box>
  )
}

export const SidebarLayoutDraft = ({ crmObject }: { crmObject: any }) => {
  const { data } = useQuery(GET_DRAFT, {
    variables: {
      id: crmObject.objectId,
      workspaceId: crmObject.workspaceId,
    },
  })

  const draft = data?.workspaceDraft

  return (
    <PageProvider
      pageId={draft?.page?.id}
      workspaceId={crmObject.workspaceId}
    >
      {draft ? <SidebarLayoutDraftInner draft={draft} /> : <SidebarLoader />}
    </PageProvider>
  )
}
